import React, { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import Like from "./Like";
import View from "./View";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IoBook } from "react-icons/io5";
import ReactPlayer from "react-player/lazy";
import { useTranslation } from "react-i18next";
import { BiSolidTimeFive } from "react-icons/bi";
import useMobileSize from "../hooks/useMobileSize";
import { Breadcrumbs } from "@material-tailwind/react";
import { ASSET_URI, fetchData, PROXY } from "../lib/utils";
import { UnderlineTabs } from "../components/UnderLineTabs";
import { FaAngleRight, FaRegCalendarAlt, FaUser } from "react-icons/fa";

import ReviewForm from "./ReviewForm";
import StarRating from "./StarRating";
import ReviewCard from "./ReviewCard";
import useAuth from "../hooks/useAuth";
import CourseEnroll from "./CourseEnroll";
import VideoSkeleton from "./skeleton/VideoSkeleton";
import SingleCourseSkeleton from "./skeleton/SingleCourseSkeleton";
import jalaali from "jalaali-js";
import CourseLoginModal from "./CourseLoginModal";
import { clearPendingCourseAction, savePendingCourseAction } from "../lib/pendingCourseAction";

const SingleCourse = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useMobileSize();
  const { isAuth } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const fetcher = (url) => fetchData(url, "accessToken");
  const { data, error, isLoading, mutate } = useSWR(`${PROXY}/courses/client/${id}`, fetcher);
  const [loginOpen, setLoginOpen] = useState(false);
  const closeLogin = useCallback((clear = true) => {
    setLoginOpen(false);
    if (clear !== false) clearPendingCourseAction();
  }, []);
  useEffect(() => { if (isAuth) setLoginOpen(false); }, [isAuth]);

  const requireCourseLogin = useCallback((action, draft) => {
    if (!data?._id) return;
    const saved = savePendingCourseAction({
      action, courseId: String(data._id),
      returnPath: action === "review"
        ? `${location.pathname}${location.search}#course-review-form`
        : `${location.pathname}${location.search}${location.hash}`, draft,
    });
    const messageKey = action === "like" ? "courseGuest.likeLoginRequired"
      : action === "start" ? "courseGuest.startLoginRequired" : "courseGuest.reviewLoginRequired";
    toast.info(t(messageKey));
    if (!saved) toast.error(t("courseGuest.saveFailed"));
    setLoginOpen(true);
  }, [data?._id, location.pathname, location.search, location.hash, t]);

  // helper: returns date only (no time). Jalali for certain languages, Gregorian otherwise.
  function formatDateByLang(rawDate, lang) {
    if (!rawDate) return "";
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return "";

    const shamsiPrefixes = ["ar", "pr", "ps", "fa", "prs"];
    const isShamsi = shamsiPrefixes.some((p) => lang?.toLowerCase?.().startsWith?.(p));

    if (!isShamsi) {
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }).format(date);
    }

    const { jy, jm, jd } = jalaali.toJalaali(date.getFullYear(), date.getMonth() + 1, date.getDate());
    const dariMonths = [
      "حمل", "ثور", "جوزا", "سرطان", "اسد", "سنبله",
      "میزان", "عقرب", "قوس", "جدی", "دلو", "حوت",
    ];
    const monthName = dariMonths[jm - 1] ?? jm;
    return `${jd} ${monthName} ${jy}`;
  }

  const createdAtFormatted = useMemo(
    () => formatDateByLang(data?.createdAt, i18n.language || ""),
    [data?.createdAt, i18n.language]
  );

  const universityName =
    data?.university?.name?.[i18n.language] ||
    data?.university?.name?.en ||
    data?.university?.name?.pr ||
    data?.university?.name?.ps ||
    "";

  if (error) return <div>{t("Error")}: {error.message}</div>;
  if (isLoading) return <SingleCourseSkeleton />;
  if (!data || !data.category) return <div>{t("No course data available")}</div>;

  return (
    <>
      <div className="px-4 mx-auto sm:max-w-5xl sm:px-6 lg:max-w-7xl lg:px-8 max-w-7xl rounded-xl dark:bg-gray-800 dark:border-gray-700 mt-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 lg:grid-cols-6 gap-4">
          <div className="sm:col-span-1 md:col-span-6 lg:col-span-3 my-5">
            {data?.category && (
              <Breadcrumbs separator={<FaAngleRight />}>
                <Link to="/" className="hover:text-gray-700 opacity-60">{t("Home")}</Link>
                <Link to="#" className="hover:text-gray-700 opacity-60">{data.category.name?.[i18n.language]}</Link>
                <Link to="#" className="hover:text-gray-700">
                  {Array.isArray(data.category.subCategories) && data.category.subCategories.length > 0
                    ? data.category.subCategories[0]?.[i18n.language]
                    : ""}
                </Link>
              </Breadcrumbs>
            )}

            <div className="flex justify-between items-center">
              <img
                className="rounded h-1/4 mt-5"
                src={`${ASSET_URI}/${data?.image}`}
                alt="data"
                height="150"
                width="150"
                crossOrigin="anonymous"
                loading="lazy"
              />

              <div className="flex flex-col lg:flex-row lg:justify-center items-center gap-3">
                <View mutate={mutate} views={data?.views} courseId={data?._id} />
                <Like mutate={mutate} likes={data?.likes} likesCount={data?.likesCount} courseId={data?._id}
                  onLoginRequired={requireCourseLogin} />
                <StarRating reviewLength={data?.totalReviews} averageRating={data?.averageRating} />
              </div>
            </div>

            <h1 className="sm:text-lg md:text-2xl lg:text-4xl font-bold mt-3 mb-5">
              {data?.title?.[i18n.language] || t("Untitled")}
            </h1>
            {universityName ? (
              <p className="mb-3 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                {t("courseSearch.university")}: {universityName}
              </p>
            ) : null}
            <h1 className="sm:text-md md:text-lg lg:text-xl mb-8">
              {data?.slogan?.[i18n.language]}
            </h1>

            <CourseEnroll courseId={id} onLoginRequired={requireCourseLogin} />
          </div>

          <div className="sm:col-span-1 md:col-span-2 lg:col-span-3 my-5">
            <div className="w-full lg:w-full rounded-lg">
              <ReactPlayer
                fallback={<VideoSkeleton />}
                url={`${PROXY}/courses/stream?p=${data?.demo}`}
                light={
                  <div className="relative w-full h-full">
                    <img src={`${ASSET_URI}/${data?.image}`} alt="Thumbnail" crossOrigin="anonymous" className="w-full h-full rounded-lg" />
                    <div className="absolute inset-0 bg-black opacity-50 rounded-lg"></div>
                  </div>
                }
                config={{ file: { attributes: { crossOrigin: "true" } } }}
                playing
                controls
                crossOrigin="anonymous"
                width="100%"
                height={`${isMobile ? "auto" : "380px"}`}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-6 md:grid-cols-6 lg:grid-cols-4 gap-4 mt-5">
          <div className="flex items-center gap-2">
            <BiSolidTimeFive size={25} />
            <div>
              <p className="font-bold text-lg">{t("Hours")}</p>
              <p className="text-lg">{data?.duration}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <IoBook size={25} />
            <div>
              <p className="font-bold text-lg">{t("videos")}</p>
              <p className="text-lg">{data?.chapter}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <FaRegCalendarAlt size={25} />
            <div>
              <p className="font-bold text-lg">{t("Date")}</p>
              <p className="text-lg">{createdAtFormatted}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <FaUser size={25} />
            <div>
              <p className="font-bold text-lg">{t("Instructor")}</p>
              <p className="text-lg">{data?.instructor?.fullName || t("Unknown Instructor")}</p>
            </div>
          </div>
        </div>

        <UnderlineTabs desc={data?.desc} curriculum={data?.curriculum} />
        <ReviewForm mutate={mutate} courseId={data?._id} reviews={data?.reviews}
          onLoginRequired={requireCourseLogin} />

        <div id="reviews" className="max-w-xl">
          <h2 className="text-sm lg:text-2xl font-semibold mb-4">
            {t("Course-Reviews")}
            <sub className="text-xs mx-1">{data?.totalReviews?.toLocaleString("en-us")}</sub>
          </h2>

          <ReviewCard mutate={mutate} courseId={data?._id} reviews={data?.reviews} averageRating={data?.averageRating} />
        </div>
      </div>
      <CourseLoginModal open={loginOpen} onClose={closeLogin} />
    </>
  );
};

export default SingleCourse;
